// @flow
import * as React from 'react';
import {useHistory, useParams} from 'react-router-dom';
import styled from 'styled-components';
import Image, {BackgroundImage} from 'components/Image';
import {LayoutCenterMiddle, LayoutWrapper} from 'components/Layout';
import {
  LocalizedParagraph,
  Paragraph,
  useLocalized,
} from 'components/Localized';
import Logo from 'components/Logo';
import {useExteriorColorIndex} from 'components/Session';
import Space from 'components/Space';
import Barcode from 'assets/images/survey/img-id-barcode.svg';
import Disclaimer from 'components/Disclaimer'
import * as AudioUtils from 'utils/audioUtils';
import SoundToggleButton from 'components/SoundToggleButton';
import ButtonNextPage from 'components/ButtonNextPage';

type Props = {
  nextPathA: string,
  nextPathB: string,
};

export default function DrivingPass(props: Props): React.Element<any> {
  const history = useHistory();
  const localized = useLocalized();
  const {cv} = useParams();
  const [disclaimerOpened, setDisclaimerOpened] = React.useState(0);
  const data = React.useMemo(() => {
    if (cv === 'cv-e-gt') {
      return [
        "tutorial_p03_pass_210308_018",
        "tutorial_p03_pass_210308_035",
        "tutorial_p03_pass_210308_033",
      ];
    }
    return [
      "tutorial_p03_pass_210308_019",
      "tutorial_p03_pass_210308_036",
      "tutorial_p03_pass_210308_034",
      ];
  }, [cv]);

  const handleDisclaimer = React.useCallback((type: number) => {
    setDisclaimerOpened(type);
  }, [])

  React.useEffect(()=>{
    AudioUtils.playBGM_UI();
    return ()=>{
    };
  }, []);
  
  const carColorIndex = useExteriorColorIndex();
  const carImages = cv === 'cv-e-gt' ? [
    './survey/img-egt-abp.png',
    './survey/img-egt-swp.png',
    './survey/img-egt-crs.png',
    './survey/img-egt-klm.png',
    './survey/img-egt-du3.png',
  ] :
  [
    './survey/img-gtl-abp.png',
    './survey/img-gtl-swp.png',
    './survey/img-gtl-crs.png',
    './survey/img-gtl-klm.png',
    './survey/img-gtl-du3.png',
  ];

  const handleNext = React.useCallback(() => {
    if (cv === 'cv-e-gt') {
      history.push(props.nextPathA);
    }
    else {
      history.push(props.nextPathB);
    }
  }, [history, props, cv])

  React.useEffect(()=>{
    window.tagManager.drivingPassPage();
  },[]);
  console.log("DrivingPass:disclaimerOpened=", disclaimerOpened);

  return (
    <main>
      <DivBack />
      <Wrapper>
          <Paragraph textKey="tutorial_p03_pass_210308_004" />
          <DivLicense source="./survey/img-id-bg.png">
            <div>
              <DivPass>
                <div>
                  <Paragraph textKey="tutorial_p03_pass_210308_001" />
                </div>
                <DivDesc isEGT={cv === 'cv-e-gt'}>
                  <div>
                    {cv === 'cv-e-gt' ? <DescEGT onDisclaimer={handleDisclaimer} /> : <DescGTL onDisclaimer={handleDisclaimer} />}
                  </div>
                  <div>
                    <Image source={carImages[carColorIndex ?? 0]} />
                  </div>
                </DivDesc>
                <div>
                  <Paragraph textKey={data[0]} />
                </div>
                <DivLine />
              </DivPass>
            </div>
            <div>
              <DivPass2>
                <div>
                  <LocalizedParagraph {...localized.tutorial_p03_survey_027} />
                </div>
                <DivAlignCenter>
                  <Paragraph textKey={data[1]} />
                  <div>
                    <Paragraph textKey={data[2]} />
                  </div>
                </DivAlignCenter>
                <DivBarcode image={Barcode} />
                <DivLine />
              </DivPass2>
            </div>
          </DivLicense>
      </Wrapper>
      <SoundToggleButton />
      <ButtonNextPage
        title={localized.tutorial_p03_survey_031}
        onNext={handleNext}
      />
      <Logo />
      { disclaimerOpened === 1 && 
      <Disclaimer popupTextKey="disclaimer_survey_eGT_popup" onPopupClose={()=>{
        setDisclaimerOpened(0);
      }} />
      }
      { disclaimerOpened === 2 && 
      <Disclaimer popupTextKey="disclaimer_survey_GTL_popup" onPopupClose={()=>{
        setDisclaimerOpened(0);
      }} />
      }
      { disclaimerOpened === 3 && 
      <Disclaimer popupTextKey="disclaimer_survey_GTL2_popup" onPopupClose={()=>{
        setDisclaimerOpened(0);
      }} />
      }
      { disclaimerOpened === 0 && 
        <Disclaimer textKey="disclaimer_default" />
      }

      
    </main>
  );
}

type PropsDesc = {
  onDisclaimer: (number) => void,
}

function DescEGT(props: PropsDesc) {
  return <dl>
    <Space heightM={12} heightD={24} />
    <dt><Paragraph textKey="tutorial_p03_pass_210308_011" /></dt>
    <dd><Paragraph textKey="tutorial_p03_pass_210308_021" /></dd>
    <dt>
      <Paragraph textKey="tutorial_p03_pass_210308_012" />
    </dt>
    <dd>
      <Paragraph textKey="tutorial_p03_pass_210308_022" />
    </dd>
    <dt>
      <Paragraph textKey="tutorial_p03_pass_210308_013" />
    </dt>
    <dd>
      <Paragraph textKey="tutorial_p03_pass_210308_023" />
    </dd>
    <dt>
      <Paragraph textKey="tutorial_p03_pass_210308_014" />
    </dt>
    <dd>
      <Paragraph textKey="tutorial_p03_pass_210308_024" onClick={() => props.onDisclaimer(1)}/>
    </dd>
    <DivRowTable>
      <div>
        <dt>
          <Paragraph textKey="tutorial_p03_pass_210308_015" />
        </dt>
        <dd>
          <Paragraph textKey="tutorial_p03_pass_210308_025" />
        </dd>
      </div>
      <div>
        <dt><Paragraph textKey="tutorial_p03_pass_210419_001" /></dt>
        <dd><Paragraph textKey="tutorial_p03_pass_210419_002" /></dd>
      </div>
    </DivRowTable>
  </dl>
}

function DescGTL(props: PropsDesc) {
  return <dl>
    <Space heightM={12} heightD={24} />
    <dt><Paragraph textKey="tutorial_p03_pass_210308_032" /></dt>
    <dd></dd>
    <Space heightM={3} heightD={0} />
    <dt>
      <Paragraph textKey="tutorial_p03_pass_210308_012" />
    </dt>
    <dd>
      <Paragraph textKey="tutorial_p03_pass_210308_022" />
    </dd>
    <dt>
      <Paragraph textKey="tutorial_p03_pass_210308_016" />
    </dt>
    <dd>
      <Paragraph textKey="tutorial_p03_pass_210308_027" onClick={() => props.onDisclaimer(2)}/>
    </dd>
    <dt>
      <Paragraph textKey="tutorial_p03_pass_210308_017" />
    </dt>
    <dd>
      <Paragraph textKey="tutorial_p03_pass_210308_028" onClick={() => props.onDisclaimer(3)}/>
    </dd>
    <dt><Paragraph textKey="tutorial_p03_pass_210419_001" /></dt>
    <dd><Paragraph textKey="tutorial_p03_pass_210419_002" /></dd>
  </dl>
}

const Wrapper = styled(LayoutCenterMiddle)`
  flex-direction: column;

  > p {
    position: relative;
    padding-bottom: ${(props) => (props.theme.layout.mobile ? 9 : 24)}px;
  }
`;

const DivBack = styled(LayoutWrapper)`
  background-color: #05141f;
`;

const DivLicense = styled(BackgroundImage)`
  width: ${(props) => (props.theme.layout.mobile ? 451 : 902)}px;
  height: ${(props) => (props.theme.layout.mobile ? 216 : 431)}px;
  margin-bottom: 22px;

  background-size: cover;
  background-repeat: no-repeat;
  position: relative;
  display: flex;
  flex-direction: row;

  > :first-child {
    width: ${(props) => (props.theme.layout.mobile ? 273 : 540)}px;
    padding: ${(props) => (props.theme.layout.mobile ? 0 : 0)}px
      ${(props) => (props.theme.layout.mobile ? 22 : 44)}px
      ${(props) => (props.theme.layout.mobile ? 15 : 29)}px
      ${(props) => (props.theme.layout.mobile ? 22 : 44)}px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  > :last-child {
    width: ${(props) => (props.theme.layout.mobile ? 95 : 190)}px;
    padding: ${(props) => (props.theme.layout.mobile ? 0 : 0)}px
      ${(props) => (props.theme.layout.mobile ? 22 : 44)}px
      ${(props) => (props.theme.layout.mobile ? 15 : 29)}px
      ${(props) => (props.theme.layout.mobile ? 22 : 44)}px;
    display: flex;

    > div {
      flex: 1;
    }

  }
`;

const DivPass = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  > :nth-child(1) {
    height: ${(props) => (props.theme.layout.mobile ? 35 : 70)}px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  > :nth-child(2) {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  > :nth-child(3) {
    height: ${(props) => (props.theme.layout.mobile ? 23 : 46)}px;
    padding: 0px ${(props) => (props.theme.layout.mobile ? 10 : 19)}px;
    margin-bottom: ${(props) => (props.theme.layout.mobile ? 5 : 10)}px;
    display: flex;
    align-items: center;
    justify-content: start;

    background-color: #e2e4e6;
  }

  border-bottom: 1px solid #ffffff;
`;

const DivPass2 = styled(DivPass)`
> :nth-child(3) {
  background-color: transparent;
}
  
`;

const DivAlignCenter = styled.div`
  margin-top: ${(props) => (props.theme.layout.mobile ? 17 : 33)}px;
  text-align: center;

  display: flex;
  flex-direction: column;

  > div {
    margin-top: ${(props) => (props.theme.layout.mobile ? 18 : 36)}px;
    margin-bottom: ${(props) => (props.theme.layout.mobile ? 7 : 14)}px;
    padding: ${(props) => (props.theme.layout.mobile ? 4 : 8)}px ${(props) => (props.theme.layout.mobile ? 5 : 10)}px;
    background-color: #e2e4e6;
  }
`;

const DivDesc = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;

  > :first-child {
    flex: 1;
    margin: 0px
      ${(props) => (props.theme.layout.mobile ? 11 : 19)}px;
    margin-right: 0px;

    dt {
      padding-bottom: ${(props) => (props.theme.layout.mobile ? 1 : 2)}px;
    }

    dd {
      padding-bottom: ${(props) => (props.theme.layout.mobile ? 6 : 12)}px;
    }

    > :last-child {
      pointer-events:auto;
      
    }
  }

  > :last-child {
    position: absolute;
    right: 0px;
    bottom: ${(props) => (props.theme.layout.mobile ? props.isEGT ? 40 : 12 : props.isEGT ? 79 : 24)}px;

    height: ${(props) => (props.theme.layout.mobile ? 73 : 145)}px;

    img {
      height: 100%;
    }
  }

  button {
    text-align: left;
  }
`;

const DivBarcode = styled.div`
  background-image: url(${(props) => props.image});
  background-size: cover;
`;

const DivLine = styled.div`
  height: ${(props) => (props.theme.layout.mobile ? 1 : 2)}px;
  background-color: #2a3740;
`;

const DivRowTable = styled.div`
  display: flex;
  flex-direction: row;

  > :nth-child(1) {
    flex: 1;
  }

  > :nth-child(2) {
    width: ${(props) => (props.theme.layout.mobile ? 138 : 265)}px;
  }
`;